import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  useColorModeValue,
  Spinner,
  HStack,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useSearch } from '../../../contexts/SearchContext';
import { useTags } from '../../../contexts/TagsContext';
import { useEncryption } from '../../../contexts/EncryptionContext';

const SearchResults: React.FC = () => {
  const { searchResults, isSearching } = useSearch();
  const { tags } = useTags();
  const { decrypt } = useEncryption();
  const [decryptedPreviews, setDecryptedPreviews] = useState<Record<string, string>>({});
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    const decryptEntries = async () => {
      const previews: Record<string, string> = {};
      for (const entry of searchResults) {
        try {
          const decrypted = await decrypt(entry.encrypted_content);
          previews[entry.id] = decrypted.substring(0, 150) + '...';
        } catch (error) {
          console.error('Error decrypting entry:', error);
          previews[entry.id] = 'Error decrypting entry';
        }
      }
      setDecryptedPreviews(previews);
    };
    decryptEntries();
  }, [searchResults, decrypt]);

  if (isSearching) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (searchResults.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No entries found</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {searchResults.map((result) => (
        <Box
          key={result.id}
          p={4}
          bg={bgColor}
          borderRadius="md"
          border="1px solid"
          borderColor={borderColor}
          _hover={{ borderColor: 'blue.500' }}
          cursor="pointer"
        >
          <Text fontSize="sm" color="gray.500" mb={2}>
            {format(new Date(result.entry_date || ''), 'MMMM d, yyyy')}
          </Text>
          
          <Text noOfLines={2} mb={2}>
            {decryptedPreviews[result.id] || 'Decrypting...'}
          </Text>

          <HStack spacing={2} flexWrap="wrap">
            {result.decrypted_mood_label && (
              <Tag size="sm" colorScheme="blue" variant="subtle">
                <TagLabel>Mood: {result.decrypted_mood_label}</TagLabel>
              </Tag>
            )}
            
            {result.tags?.map((tagId) => {
              const tag = tags.find((t) => t.id === tagId);
              return (
                tag && (
                  <Tag
                    key={tagId}
                    size="sm"
                    colorScheme="gray"
                    variant="subtle"
                  >
                    <TagLabel>{tag.decrypted_name}</TagLabel>
                  </Tag>
                )
              );
            })}

            {/* {result.location_lat && (
              <Tag size="sm" colorScheme="green" variant="subtle">
                <TagLabel>{typeof result.location_lat === 'string' ? result.location_lat : 'Location'}</TagLabel>
              </Tag>
            )} */}
          </HStack>
        </Box>
      ))}
    </VStack>
  );
};

export default SearchResults; 